import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import AlertModal from 'components/common/AlertModal';
import ConfirmModal from 'components/common/ConfirmModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import StatusHeader from 'components/common/StatusHeader';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { displayError, handleServiceError } from 'utils/axiosError';
import DisplayTimingGroupDetailModal from '../components/DisplayTimingGroupDetailModal';
import DisplayTimingGroupFilter, { SearchDisplayTimingGroupFilter } from '../components/DisplayTimingGroupFilter';
import DisplayTimingGroupTable from '../components/DisplayTimingGroupTable';
import displayTimingGroupService from '../displayTimingGroupService';
import { DisplayTimingGroupListItem, UpdateSortOrderPayload } from '../models';

function DisplayTimingGroupListPage() {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  //ref
  const arrChkSelectRef = useRef<Array<HTMLInputElement>>([]);
  const btnDeleteRef = useRef<HTMLButtonElement>(null);
  const btnAddNewRef = useRef<HTMLButtonElement>(null);
  const btnCopyRef = useRef<HTMLButtonElement>(null);
  const btnViewDetailRef = useRef<HTMLButtonElement>(null);
  const btnSaveRef = useRef<HTMLButtonElement>(null);

  // states
  const [filter, setFilter] = useState<SearchDisplayTimingGroupFilter | null>(null);
  const [dataList, setDataList] = useState<DisplayTimingGroupListItem[]>([]);

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailModalData, setDetailModalData] = useState<{ id?: number; copyFromId?: number }>();

  function handleFilter(data: SearchDisplayTimingGroupFilter) {
    setFilter(data);
  }

  // functions
  const loadDataList = useCallback(() => {
    (async () => {
      try {
        const response = await displayTimingGroupService.getAll({
          active: filter?.active || '0',
          group_name: filter?.group_name || '',
        });
        if (response.data) {
          setDataList(response.data);
        }
        resetSelectRow();
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, showGlobalModal]);

  function getSelectedElements() {
    return arrChkSelectRef.current.filter((elem) => elem && elem.checked);
  }

  // handlers
  function processDelete(idsToDelete: number[]) {
    (async () => {
      try {
        const response = await displayTimingGroupService.delete(idsToDelete);
        showGlobalModal(() => <AlertModal title={t('App.info')} message={response.message} />);
        loadDataList();
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  function handleDelete() {
    const checkedElements = getSelectedElements();
    if (checkedElements.length > 0) {
      const idsToDelete = checkedElements.map((elem) => parseInt(elem.value, 10));
      // call API to get belong menus
      (async () => {
        try {
          const response = await displayTimingGroupService.getBelongMenus(idsToDelete);
          if (response.data) {
            const belongMenus = response.data;
            const groupNameCanNotDeletes = belongMenus
              .filter((item) => item.menu_count > 0 && `${item.group_name}`)
              .map((item) => item.group_name);
            const idsToDeleteFiltered = belongMenus.filter((item) => item.menu_count === 0).map((item) => item.id);
            if (groupNameCanNotDeletes.length > 0) {
              if (idsToDeleteFiltered.length > 0) {
                showGlobalModal(() => (
                  <ConfirmModal
                    title={t('App.warning')}
                    message={t('DisplayTimingGroup.confirm_delete_condition', {
                      group_names: groupNameCanNotDeletes.join('、 '),
                    })}
                    onConfirm={() => {
                      if (idsToDeleteFiltered.length > 0) {
                        processDelete(idsToDeleteFiltered);
                      }
                    }}
                  />
                ));
              } else {
                showGlobalModal(() => (
                  <AlertModal
                    title={t('App.warning')}
                    message={t('DisplayTimingGroup.confirm_delete_not_allowed', {
                      group_names: groupNameCanNotDeletes.join('、 '),
                    })}
                  />
                ));
              }
            } else {
              showGlobalModal(() => (
                <ConfirmModal
                  title={t('App.confirm')}
                  message={t('DisplayTimingGroup.confirm_delete')}
                  onConfirm={() => {
                    processDelete(idsToDelete);
                  }}
                />
              ));
            }
          }
        } catch (e) {
          if (!handleServiceError(showGlobalModal, e as AxiosError)) {
            displayError(showGlobalModal, e as AxiosError);
          }
        }
      })();
    }
  }

  function handleAddNew() {
    setDetailModalData({ id: undefined });
    setShowDetailModal(true);
  }

  function handleCopy() {
    const checkedElements = getSelectedElements();
    if (checkedElements.length === 1) {
      const idToCopy = parseInt(checkedElements[0].value, 10);
      const itemCopy = dataList.find((item) => item.id === idToCopy);
      if (itemCopy) {
        // show confirm before do copy
        showGlobalModal(() => (
          <ConfirmModal
            title={t('App.confirm')}
            message={t('DisplayTimingGroup.confirm_copy', { name: itemCopy.group_name })}
            onConfirm={() => {
              setDetailModalData({ copyFromId: idToCopy });
              setShowDetailModal(true);
            }}
          />
        ));
      }
    }
  }

  function handleDetail() {
    const checkedElements = getSelectedElements();
    if (checkedElements.length === 1) {
      const idToView = parseInt(checkedElements[0].value, 10);
      setDetailModalData({ id: idToView });
      setShowDetailModal(true);
    }
  }

  const handleReorder = useCallback((rows: DisplayTimingGroupListItem[]) => {
    setDataList(rows);
  }, []);

  function handleSave() {
    (async () => {
      const payload: UpdateSortOrderPayload[] = dataList.map((item) => ({
        id: item.id,
        sort_order: item.sort_order,
      }));

      try {
        const response = await displayTimingGroupService.updateSortOrder(payload);
        showGlobalModal(() => <AlertModal title={t('App.info')} message={response.message} />);
        loadDataList();
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  const updateStatusActionButtons = useCallback(() => {
    const checkedElements = getSelectedElements();
    if (btnViewDetailRef.current && btnDeleteRef.current && btnCopyRef.current) {
      if (checkedElements.length === 0) {
        btnViewDetailRef.current.disabled = true;
        btnDeleteRef.current.disabled = true;
        btnCopyRef.current.disabled = true;
      } else if (checkedElements.length === 1) {
        btnViewDetailRef.current.disabled = false;
        btnDeleteRef.current.disabled = false;
        btnCopyRef.current.disabled = false;
      } else {
        btnViewDetailRef.current.disabled = true;
        btnDeleteRef.current.disabled = false;
        btnCopyRef.current.disabled = true;
      }
    }
  }, []);

  function resetSelectRow() {
    arrChkSelectRef.current.forEach((elem) => {
      if (elem) {
        elem.checked = false;
      }
    });
    updateStatusActionButtons();
  }

  function handleDetailModalClose(isDataChanged: boolean) {
    setShowDetailModal(false);
    setDetailModalData(undefined);
    if (isDataChanged) {
      loadDataList();
    }
  }

  // effects
  useEffect(() => {
    if (btnViewDetailRef.current && btnDeleteRef.current && btnCopyRef.current) {
      btnViewDetailRef.current.disabled = true;
      btnDeleteRef.current.disabled = true;
      btnCopyRef.current.disabled = true;
    }
  }, []);

  useEffect(() => {
    loadDataList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  return (
    <>
      <div className="page-wrapper">
        <div className="page-header">
          <StatusHeader />
        </div>
        <div className={`page-main ${isEditMode ? 'reedit-mode-wrapper' : ''}`}>
          <div className="page-content store-setting-page d-flex flex-column">
            <div className="mb-3">
              <FeatureTitleBar title={t('DisplayTimingGroup.head_title')}></FeatureTitleBar>
            </div>
            <DisplayTimingGroupFilter onFilter={handleFilter} />
            <div style={{ fontSize: '0.875rem' }}>
              <div className="fw-bold mb-1">■ {t('DisplayTimingGroup.legend_title')}</div>
              <div>
                {t('DisplayTimingGroup.legend_weekday_sample')} … {t('DisplayTimingGroup.legend_no_background')} ＝{' '}
                {t('DisplayTimingGroup.include')}
              </div>
              <div>
                <span className="d-inline-block bg-violet p-1 rounded">
                  {t('DisplayTimingGroup.legend_holiday_sample')}
                </span>{' '}
                … {t('DisplayTimingGroup.legend_has_background')} ＝ {t('DisplayTimingGroup.exclude')}
              </div>
            </div>
            <div className="d-flex justify-content-between align-items-center mt-3 mb-2">
              <div>
                <Button ref={btnSaveRef} className="btn--dark-green" onClick={handleSave}>
                  {t('DisplayTimingGroup.save')}
                </Button>
              </div>
              <div className="d-flex">
                <Button ref={btnCopyRef} className="btn--yellow ms-3" onClick={handleCopy}>
                  {t('DisplayTimingGroup.copy')}
                </Button>
                <Button ref={btnDeleteRef} className="btn--red ms-3" onClick={handleDelete}>
                  {t('DisplayTimingGroup.delete')}
                </Button>
                <Button ref={btnViewDetailRef} className="ms-5" onClick={handleDetail}>
                  {t('DisplayTimingGroup.detail')}
                </Button>
                <Button ref={btnAddNewRef} className="btn--dark-green ms-3" onClick={handleAddNew}>
                  {t('DisplayTimingGroup.add_new')}
                </Button>
              </div>
            </div>
            <DisplayTimingGroupTable
              data={dataList}
              arrChkSelectRef={arrChkSelectRef}
              onSelectRow={updateStatusActionButtons}
              onReorder={handleReorder}
              onReloadData={loadDataList}
            />
          </div>
        </div>
      </div>
      {showDetailModal && detailModalData && (
        <DisplayTimingGroupDetailModal
          onClose={handleDetailModalClose}
          displayTimingGroupId={detailModalData.id}
          copyFromId={detailModalData.copyFromId}
        />
      )}
    </>
  );
}

export default DisplayTimingGroupListPage;
